/**
 * Pure geometry helpers for MapLens.
 * Ported from the v6 reference mock
 * (docs/superpowers/specs/assets/2026-07-05-braid-mock-v6.html) — the SVG
 * viewBox, point projection, and drift-arrow geometry come from there.
 */

export const MAP_VIEW_W = 680;
export const MAP_VIEW_H = 430;

const PROJ_PAD_X = 60;
const PROJ_SPAN_X = MAP_VIEW_W - 120; // 560
const PROJ_PAD_Y = 44;
const PROJ_SPAN_Y = MAP_VIEW_H - 96; // 334

export const DRIFT_ARROW_LEN = 10;
export const DRIFT_ARROW_ANGLE = 2.6; // radians, ported from the mock

/** Project a normalized moment (x,y in [0,1]) onto the 680x430 map viewBox. */
export function projectMoment(x: number, y: number): [number, number] {
  return [x * PROJ_SPAN_X + PROJ_PAD_X, (1 - y) * PROJ_SPAN_Y + PROJ_PAD_Y];
}

export interface Ellipse {
  cx: number;
  cy: number;
  rx: number; // always the major (larger-variance) radius
  ry: number; // always the minor (smaller-variance) radius
  angleDeg: number; // rotation of the rx axis, normalized to (-90, 90]
}

/**
 * Covariance ellipse over a point cloud, scaled by nsig standard deviations:
 * mean + closed-form 2x2 eigendecomposition (trace/determinant — no matrix
 * library needed for a symmetric 2x2). Returns null for fewer than 3 points
 * (a "territory" needs a real shape, not a line or a point).
 */
export function covEllipse(
  points: Array<[number, number]>,
  nsig: number,
): Ellipse | null {
  const n = points.length;
  if (n < 3) return null;

  let mx = 0;
  let my = 0;
  for (const [x, y] of points) {
    mx += x;
    my += y;
  }
  mx /= n;
  my /= n;

  let varX = 0;
  let varY = 0;
  let covXY = 0;
  for (const [x, y] of points) {
    const dx = x - mx;
    const dy = y - my;
    varX += dx * dx;
    varY += dy * dy;
    covXY += dx * dy;
  }
  varX /= n;
  varY /= n;
  covXY /= n;

  const trace = varX + varY;
  const det = varX * varY - covXY * covXY;
  const disc = Math.sqrt(Math.max(0, (trace / 2) ** 2 - det));
  const lambda1 = trace / 2 + disc; // larger eigenvalue -> major axis
  const lambda2 = trace / 2 - disc; // smaller eigenvalue -> minor axis

  let angleRad: number;
  if (covXY === 0) {
    angleRad = varX >= varY ? 0 : Math.PI / 2;
  } else {
    // Eigenvector for lambda1 of [[varX, covXY], [covXY, varY]]:
    // (varX - lambda1) v1 + covXY v2 = 0  =>  v2/v1 = (lambda1 - varX) / covXY
    angleRad = Math.atan2(lambda1 - varX, covXY);
  }
  // Ellipses are symmetric under a 180deg rotation - normalize to (-90, 90].
  let angleDeg = (angleRad * 180) / Math.PI;
  angleDeg = ((((angleDeg + 90) % 180) + 180) % 180) - 90;

  return {
    cx: mx,
    cy: my,
    rx: nsig * Math.sqrt(Math.max(0, lambda1)),
    ry: nsig * Math.sqrt(Math.max(0, lambda2)),
    angleDeg,
  };
}

/** Mean point of a cloud (used for drift-arrow centroids). */
export function centroid(points: Array<[number, number]>): [number, number] {
  if (points.length === 0) {
    throw new Error("[mapGeometry] centroid needs at least one point");
  }
  let sx = 0;
  let sy = 0;
  for (const [x, y] of points) {
    sx += x;
    sy += y;
  }
  return [sx / points.length, sy / points.length];
}

/**
 * Chronological early/recent split of an already-sorted list. An odd count
 * gives the extra item to the recent half.
 */
export function splitChronological<T>(sorted: T[]): [T[], T[]] {
  const earlyCount = Math.floor(sorted.length / 2);
  return [sorted.slice(0, earlyCount), sorted.slice(earlyCount)];
}

export interface DriftArrow {
  startX: number;
  startY: number;
  endX: number;
  endY: number;
  arrow1: [number, number];
  arrow2: [number, number];
  labelX: number;
  labelY: number;
}

/**
 * Dashed drift line from an early-half centroid to a recent-half centroid,
 * plus a two-stroke arrowhead at the end. Ported 1:1 from the mock's
 * `.drift` markup: a start circle, a dashed shaft, and two 10px arrowhead
 * strokes at +-2.6rad from the shaft's reverse direction.
 */
export function driftArrow(
  start: [number, number],
  end: [number, number],
): DriftArrow {
  const [startX, startY] = start;
  const [endX, endY] = end;
  const reverseAngle = Math.atan2(startY - endY, startX - endX);
  const arrow1: [number, number] = [
    endX + DRIFT_ARROW_LEN * Math.cos(reverseAngle + DRIFT_ARROW_ANGLE),
    endY + DRIFT_ARROW_LEN * Math.sin(reverseAngle + DRIFT_ARROW_ANGLE),
  ];
  const arrow2: [number, number] = [
    endX + DRIFT_ARROW_LEN * Math.cos(reverseAngle - DRIFT_ARROW_ANGLE),
    endY + DRIFT_ARROW_LEN * Math.sin(reverseAngle - DRIFT_ARROW_ANGLE),
  ];
  return {
    startX,
    startY,
    endX,
    endY,
    arrow1,
    arrow2,
    labelX: (startX + endX) / 2,
    labelY: (startY + endY) / 2 - 8,
  };
}

/** Ink-aging radius/opacity for a moment dot: linear in dateIndex/lastIndex. */
export function inkAge(
  dateIndex: number,
  lastIndex: number,
): { radius: number; fillOpacity: number } {
  if (lastIndex <= 0) {
    throw new Error(
      `[mapGeometry] inkAge needs at least two sessions (lastIndex=${lastIndex})`,
    );
  }
  const frac = dateIndex / lastIndex;
  return { radius: 4.2 + 3.0 * frac, fillOpacity: 0.3 + 0.7 * frac };
}

/** Territory (contour) opacity scaling with the time-window visible fraction. */
export function territoryOpacity(visibleFraction: number): number {
  return visibleFraction === 0 ? 0.08 : 0.3 + 0.7 * visibleFraction;
}

/** Territory label opacity scaling with the time-window visible fraction. */
export function labelOpacity(visibleFraction: number): number {
  return visibleFraction === 0 ? 0.15 : 0.35 + 0.65 * visibleFraction;
}
